import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Download, ShieldCheck, Users, Award, Star, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Classification display config ─────────────────────────────────────────────
const CLASS_CONFIG = {
  esi:           { label: 'ESI',          bg: '#dcfce7', color: '#15803d' },
  gsi:           { label: 'GSI',          bg: '#dbeafe', color: '#1d4ed8' },
  isv:           { label: 'ISV',          bg: '#f3e8ff', color: '#7e22ce' },
  csp:           { label: 'CSP',          bg: '#fff7ed', color: '#c2410c' },
  top50_csp:     { label: 'Top 50 CSP',   bg: '#fef3c7', color: '#b45309' },
  disti:         { label: 'Disti',        bg: '#f0fdf4', color: '#047857' },
  telco:         { label: 'Telco',        bg: '#e0f2fe', color: '#0369a1' },
  jumpstart:     { label: 'Jumpstart',    bg: '#fdf4ff', color: '#86198f' },
  frontier_club: { label: 'Frontier',     bg: '#fef9c3', color: '#92400e' },
};

const CLASS_OPTIONS = [
  { value: '',             label: 'All Classifications' },
  { value: 'esi',          label: 'ESI' },
  { value: 'gsi',          label: 'GSI' },
  { value: 'isv',          label: 'ISV' },
  { value: 'csp',          label: 'CSP' },
  { value: 'top50_csp',    label: 'Top 50 CSP' },
  { value: 'disti',        label: 'Disti' },
  { value: 'telco',        label: 'Telco' },
  { value: 'jumpstart',    label: 'Jumpstart' },
  { value: 'frontier_club',label: 'Frontier Club' },
];

function ClassBadge({ cls }) {
  const cfg = CLASS_CONFIG[cls];
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.04em',
      background: cfg.bg,
      color: cfg.color,
      marginRight: 4,
      marginBottom: 2,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 140,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: '14px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={14} color={accent} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#718096',
          textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
        {(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, total, perPage, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1;
  const to   = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid="maicpp-registry-pagination">
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7)  return `${diffDays}d ago`;
    if (diffDays < 31) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export default function MaicppPartnerRegistryPage() {
  const navigate = useNavigate();
  const [data,       setData]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);

  // Filter state
  const [classification, setClassification] = useState('');
  const [search,         setSearch]         = useState('');
  const [appliedClass,   setAppliedClass]   = useState('');
  const [appliedSearch,  setAppliedSearch]  = useState('');

  const fetchData = useCallback(async (p, cls, q) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 25 };
      if (cls) params.classification = cls;
      if (q)   params.search = q;
      const r = await axios.get(`${API}/api/partners/maicpp-registry`, { params, withCredentials: true });
      setData(r.data.data || []);
      setSummary(r.data.summary || {});
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(1, '', ''); }, []); // eslint-disable-line

  const handleApply = () => {
    setAppliedClass(classification);
    setAppliedSearch(search);
    fetchData(1, classification, search);
  };

  const handleReset = () => {
    setClassification(''); setSearch('');
    setAppliedClass(''); setAppliedSearch('');
    fetchData(1, '', '');
  };

  const handleExport = () => {
    const headers = ['POID', 'Org Name', 'Classifications', 'Seats', 'PDM', 'Source', 'Status', 'Last Updated'];
    const rows = data.map(r => [
      r.partner_one_id || '',
      `"${(r.org_name || '').replace(/"/g, '""')}"`,
      `"${(r.classifications || []).map(c => CLASS_CONFIG[c]?.label || c).join(', ')}"`,
      r.allowed_seats || 'Unlimited',
      `"${(r.pdm_reporting_manager || '').replace(/"/g, '""')}"`,
      r.source || '',
      r.is_block ? 'Blocked' : 'Active',
      r.updated_at || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'maicpp_partner_registry.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const isFiltered = appliedClass || appliedSearch;

  return (
    <div data-testid="maicpp-registry-page">
      {/* Breadcrumb */}
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt;{' '}
        <span style={{ cursor: 'pointer', color: '#3182ce' }} onClick={() => navigate('/partners')}
          data-testid="registry-back-link">
          Partner Dashboard
        </span>{' '}
        &gt; MAICPP Partner Registry
      </div>

      <div className="page-header">
        <h1>MAICPP Partner Registry</h1>
        <p>Microsoft AI Cloud Partner Program partner configurations</p>
      </div>

      {/* KPI bar */}
      <div data-testid="registry-kpi-bar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <KpiCard icon={Users}      label="Total Partners"    value={summary.total}             accent="#3182ce" />
        <KpiCard icon={ShieldCheck} label="ESI Partners"    value={summary.total_esi}          accent="#16a34a" />
        <KpiCard icon={Layers}     label="Jumpstart"         value={summary.total_jumpstart}    accent="#86198f" />
        <KpiCard icon={Award}      label="Top 50 CSP"        value={summary.total_top50_csp}    accent="#d97706" />
        <KpiCard icon={Star}       label="Frontier Club"     value={summary.total_frontier_club} accent="#ca8a04" />
      </div>

      {/* Filter row */}
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Classification</label>
          <select
            className="filter-input"
            value={classification}
            onChange={e => setClassification(e.target.value)}
            data-testid="registry-classification-select"
            style={{ minWidth: 160 }}
          >
            {CLASS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group" style={{ flex: 2 }}>
          <label className="filter-label">Search org name or POID</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              className="filter-input"
              style={{ paddingLeft: 30 }}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
              data-testid="registry-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary"   onClick={handleApply}  data-testid="registry-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset}  data-testid="registry-reset-btn">Reset</button>
          <button className="btn btn-secondary" onClick={handleExport} data-testid="registry-export-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title" style={{ fontFamily: 'monospace' }}>maicpp_partner_config</span>
            <span className="table-count" style={{ marginLeft: 8 }}>
              ({total.toLocaleString()} row{total !== 1 ? 's' : ''}{isFiltered ? ' — filtered' : ''})
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No partners found</h3>
            <p>{isFiltered ? 'Try a different filter or search term' : 'Table appears to be empty'}</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="registry-table">
                <thead>
                  <tr>
                    <th>POID</th>
                    <th>ORG NAME</th>
                    <th>CLASSIFICATIONS</th>
                    <th>SEATS</th>
                    <th>STATUS</th>
                    <th>LAST UPDATED</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.partner_one_id || i} data-testid={`registry-row-${i}`}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#3182ce', whiteSpace: 'nowrap' }}>
                        {row.partner_one_id}
                      </td>
                      <td style={{ maxWidth: 260, fontSize: 13 }}>
                        {row.org_name || <span style={{ color: '#ccc', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {(row.classifications || []).map(cls => (
                            <ClassBadge key={cls} cls={cls} />
                          ))}
                          {(row.classifications || []).length === 0 && (
                            <span style={{ color: '#ccc', fontSize: 12 }}>—</span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.allowed_seats || 'Unlimited'}
                      </td>
                      <td>
                        {row.is_block ? (
                          <span className="badge" style={{ background: '#fef2f2', color: '#dc2626',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                            Blocked
                          </span>
                        ) : (
                          <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                            Active
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>
                        {formatDate(row.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} perPage={25}
              onPage={p => fetchData(p, appliedClass, appliedSearch)} />
          </>
        )}
      </div>
    </div>
  );
}
